/**
 * Treatment Record Quick Toolbar (PRD.182).
 *
 * Shared quick-entry surface used by both the Patient Timeline and the
 * Treatment Record. Exposes:
 *   - a date chip / picker (defaults to Today)
 *   - inline Note text entry
 *   - inline Voice dictation (appends into the note input)
 *   - Add Charting & Files (opens a right drawer)
 *   - Add Invoice (opens a right drawer)
 *
 * Note + Voice persist via `ensureEncounterForDate` so writes always land in
 * the latest Visit Note for the selected date (creating one in the background
 * if none exists). The drawer-based actions surface the existing encounter
 * tabs scoped to that resolved encounter.
 */
import { useCallback, useState, type CSSProperties } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncDb } from '../../../adapters/powersync';
import { ensureEncounterForDate } from '../../../services/encounter-service';
import { useVoiceToText } from '../../../hooks/useVoiceToText';
import { DateChipPopover, todayLocalISO } from './DateChipPopover';

interface ToolbarProps {
  patientId: string;
  /** Open the Add Charting & Files drawer for the resolved encounter. */
  onOpenChartingFiles: (encounterId: string, dateISO: string) => void;
  /** Open the Add Invoice drawer for the resolved encounter. */
  onOpenInvoice: (encounterId: string, dateISO: string) => void;
}

const iconBtnStyle = (active: boolean): CSSProperties => ({
  width: 36,
  height: 36,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: active ? 'var(--color-primary-subtle, #fffbeb)' : 'var(--bg-surface)',
  color: active ? 'var(--color-primary)' : 'var(--text-muted)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
});

export function TreatmentRecordQuickToolbar({
  patientId,
  onOpenChartingFiles,
  onOpenInvoice,
}: ToolbarProps) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const [date, setDate] = useState<string>(todayLocalISO());
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2000);
  }, []);

  const resolveEncounter = useCallback(async () => {
    if (!db || !orgId || !patientId) return null;
    const { encounterId } = await ensureEncounterForDate({
      db,
      orgId,
      patientId,
      date,
    });
    return encounterId;
  }, [db, orgId, patientId, date]);

  const handleSaveNote = useCallback(async () => {
    const text = note.trim();
    if (!text || !db || saving) return;
    setSaving(true);
    try {
      const encounterId = await resolveEncounter();
      if (!encounterId) return;
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      // Postgres `patient_notes.title` is NOT NULL. Quick toolbar entries
      // don't take a separate title, so we send an empty string and store
      // the user's text in `body` only.
      await db.execute(
        `INSERT INTO patient_notes
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
           appointment_id, queue_id,
           title, body, note_type, alert_level, visibility,
           author_user_id, author_name,
           created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, NULL, NULL, NULL, ?, ?, 'general', 'none', 'team',
                 NULL, NULL, ?, ?)`,
        [id, orgId, null, patientId, encounterId, '', text, now, now],
      );
      setNote('');
      showToast('Note saved');
    } catch (err) {
      console.error('[TreatmentRecordQuickToolbar] save note:', err);
      showToast('Failed to save note');
    } finally {
      setSaving(false);
    }
  }, [note, db, saving, orgId, patientId, resolveEncounter, showToast]);

  // Voice dictation appends into the inline note input; user can review and
  // hit Save to persist (PRD.182 §8.3 / Open Q3 — append-into-note default).
  const voice = useVoiceToText('transcription', (text) => {
    if (!text) return;
    setNote((prev) => (prev ? `${prev} ${text}`.trim() : text));
  });

  const handleVoiceClick = useCallback(() => {
    if (voice.status === 'recording') {
      void voice.stop();
    } else if (voice.status === 'idle' || voice.status === 'error') {
      voice.start();
    }
  }, [voice]);

  const handleOpenChartingFiles = useCallback(async () => {
    const encounterId = await resolveEncounter();
    if (encounterId) onOpenChartingFiles(encounterId, date);
  }, [resolveEncounter, onOpenChartingFiles, date]);

  const handleOpenInvoice = useCallback(async () => {
    const encounterId = await resolveEncounter();
    if (encounterId) onOpenInvoice(encounterId, date);
  }, [resolveEncounter, onOpenInvoice, date]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          flexWrap: 'wrap',
        }}
      >
        <DateChipPopover patientId={patientId} date={date} onChange={setDate} />

        {/* Note input — inline */}
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSaveNote();
            }
          }}
          placeholder="Quick note…"
          disabled={saving}
          style={{
            flex: 1,
            minWidth: 160,
            height: 36,
            padding: '0 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            outline: 'none',
          }}
        />

        {/* Save note */}
        <button
          type="button"
          onClick={() => void handleSaveNote()}
          disabled={!note.trim() || saving}
          title="Save note"
          aria-label="Save note"
          style={{
            ...iconBtnStyle(false),
            background: note.trim()
              ? 'var(--color-primary, #2563eb)'
              : 'var(--bg-surface-2, #f5f5f5)',
            color: note.trim() ? '#fff' : 'var(--text-placeholder)',
            border: 'none',
          }}
        >
          <NavIcon name={saving ? 'refresh-cw' : 'check'} />
        </button>

        {/* Voice */}
        <button
          type="button"
          onClick={handleVoiceClick}
          title={voice.status === 'recording' ? 'Stop recording' : 'Start voice dictation'}
          aria-label="Voice"
          style={iconBtnStyle(voice.status === 'recording')}
        >
          <NavIcon name={voice.status === 'recording' ? 'mic-off' : 'mic'} />
        </button>

        {/* Add Charting & Files (drawer) */}
        <button
          type="button"
          onClick={() => void handleOpenChartingFiles()}
          title="Add Charting & Files"
          aria-label="Add Charting & Files"
          style={iconBtnStyle(false)}
        >
          <NavIcon name="camera" />
        </button>

        {/* Add Invoice (drawer) */}
        <button
          type="button"
          onClick={() => void handleOpenInvoice()}
          title="Add Invoice"
          aria-label="Add Invoice"
          style={iconBtnStyle(false)}
        >
          <NavIcon name="peso" />
        </button>
      </div>

      {(toast || voice.status === 'recording' || voice.status === 'transcribing') && (
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            paddingLeft: 4,
          }}
        >
          {toast
            ? toast
            : voice.status === 'recording'
              ? `Recording… ${voice.elapsed}s`
              : voice.status === 'transcribing'
                ? 'Transcribing…'
                : ''}
        </div>
      )}
    </div>
  );
}
